'use client'

import type { Account, RumInitConfiguration, User } from '@datadog/browser-rum'
import { addNextjsError, datadogRum, nextjsPlugin } from '#rum-sdk'
import { BROWSER_METRIC_ENDPOINT } from './browser-metric-contract'
import {
  redactSensitiveString,
  sanitizeLogValue,
  sanitizeUrl,
  truncate,
} from './core'
import { installBrowserLifecycleDrain } from './lifecycle-browser'
import { configureClientMetrics } from './metrics-browser'
import { rumCorrelationIdPaths } from './rum-correlation'
import { createRumErrorDiagnostic } from './rum-error'
import { metricRumIdPaths } from './rum-metric-context'
import { setRumVitalsEnabled } from './rum-runtime'
import { incrementTelemetryHealth } from './telemetry-health'

export { measureClientOperation } from './browser-operation'
export {
  CLIENT_METRICS,
  type ClientJourney,
  type ClientJourneyOutcome,
  isClientMetricsEnabled,
  observeClientOperation,
  recordClientDuration,
  recordClientMetric,
  startClientJourney,
} from './metrics-browser'
export { createNavigationTelemetry } from './navigation-browser'
export { createPageCommitTelemetry } from './page-commit-browser'
export {
  createPageContentTelemetry,
  type PageContentContract,
} from './page-content-browser'
export {
  comparePageRouteSpecificity,
  matchesPageRoute,
  type PageCommitContract,
} from './page-contracts'
export {
  createPageReadinessTelemetry,
  type PageReadinessState,
  usePageReadinessTelemetry,
} from './page-readiness-browser'
export { usePaginationTelemetry } from './pagination-browser'
export { useQueryTelemetry } from './query-browser'

type AllowedTracingUrl = {
  match: (url: string) => boolean
  propagatorTypes: Array<'tracecontext'>
}

export type RumSanitizableEvent = {
  type: string
  action?: { target?: { name?: string } }
  vital?: { name?: string; type?: string }
  view?: { url?: string }
  resource?: { url?: string }
  error?: {
    message?: unknown
    stack?: unknown
    handling_stack?: unknown
  }
  context?: Record<string, unknown>
}

const EXTENSION_OBJECT_NOT_FOUND_PATTERN =
  /^Uncaught "Object Not Found Matching Id:\d+, MethodName:update, ParamCount:4"$/

function normalizePrefix(prefix: string): string | null {
  try {
    const url = new URL(prefix)
    const path = url.pathname.replace(/\/$/, '')
    return `${url.origin}${path}`
  } catch {
    return null
  }
}

function matchesPrefix(url: string, prefix: string): boolean {
  return url === prefix || url.startsWith(`${prefix}/`)
}

function isBrewVercelHost(hostname: string): boolean {
  return (
    hostname.endsWith('.vercel.app') &&
    (hostname.startsWith('brew-omni-agent-mvp-') ||
      hostname.startsWith('brew-admin-'))
  )
}

function isOwnedTracingPrefix(prefix: string, origin: string): boolean {
  try {
    const candidate = new URL(prefix)
    const current = new URL(origin)
    return (
      candidate.origin === current.origin ||
      candidate.hostname === 'brew.new' ||
      candidate.hostname.endsWith('.brew.new') ||
      isBrewVercelHost(candidate.hostname)
    )
  } catch {
    return false
  }
}

export function buildAllowedTracingUrls(args: {
  origin: string
  additionalPrefixes?: Array<string>
}): Array<AllowedTracingUrl> {
  const prefixes = [args.origin, ...(args.additionalPrefixes ?? [])]
    .map((prefix) => normalizePrefix(prefix.trim()))
    .filter((prefix): prefix is string => Boolean(prefix))
    .filter((prefix) => isOwnedTracingPrefix(prefix, args.origin))

  return Array.from(new Set(prefixes)).map((prefix) => ({
    match: (url: string) => {
      try {
        if (new URL(url).pathname === BROWSER_METRIC_ENDPOINT) {
          return false
        }
      } catch {
        return false
      }
      return matchesPrefix(url, prefix)
    },
    propagatorTypes: ['tracecontext'],
  }))
}

function isKnownRumNoise(event: RumSanitizableEvent): boolean {
  if (event.type !== 'error' || typeof event.error?.message !== 'string') {
    return false
  }
  return (
    event.error.message.includes('ResizeObserver loop') ||
    EXTENSION_OBJECT_NOT_FOUND_PATTERN.test(event.error.message)
  )
}

export function sanitizeRumEvent(event: RumSanitizableEvent): boolean {
  if (isKnownRumNoise(event)) {
    return false
  }
  if (typeof event.resource?.url === 'string') {
    try {
      if (new URL(event.resource.url).pathname === BROWSER_METRIC_ENDPOINT) {
        return false
      }
    } catch {
      /* Other malformed resource URLs are sanitized below. */
    }
  }
  if (typeof event.view?.url === 'string') {
    event.view.url = sanitizeUrl(event.view.url)
  }
  if (typeof event.resource?.url === 'string') {
    event.resource.url = sanitizeUrl(event.resource.url)
  }
  if (typeof event.error?.message === 'string') {
    event.error.message = truncate(
      redactSensitiveString(event.error.message),
      2000
    )
  }
  if (typeof event.error?.stack === 'string') {
    event.error.stack = truncate(
      redactSensitiveString(event.error.stack),
      16_384
    )
  }
  if (typeof event.error?.handling_stack === 'string') {
    event.error.handling_stack = truncate(
      redactSensitiveString(event.error.handling_stack),
      16_384
    )
  }
  if (event.context) {
    event.context = sanitizeLogValue(
      event.context,
      event.type === 'error'
        ? rumCorrelationIdPaths(event.context)
        : metricRumIdPaths(event)
    ) as Record<string, unknown>
  }
  return true
}

/**
 * Parse one `NEXT_PUBLIC_DATADOG_*_SAMPLE_RATE` value.
 *
 * Unset, blank and non-numeric values mean "use the profile default", and
 * numbers are clamped to the SDK's 0–100 range. `Number('')` is `0`, so a
 * variable that merely existed with an empty value used to switch the whole
 * RUM profile off while `initDatadogRum` still reported success. An explicit
 * `'0'` is honoured: that is an operator decision, not a misconfiguration.
 */
export function parseSampleRate(
  value: string | number | null | undefined,
  fallback: number
): number {
  if (value === undefined || value === null) {
    return fallback
  }
  const raw = typeof value === 'string' ? value.trim() : value
  if (raw === '') {
    return fallback
  }
  const parsed = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isFinite(parsed)) {
    return fallback
  }
  return Math.min(100, Math.max(0, parsed))
}

export function initDatadogRum(args: {
  applicationId: string | undefined
  clientToken: string | undefined
  service: string
  env: string
  version?: string
  site?: string
  origin: string
  additionalTracingPrefixes?: Array<string>
  sessionSampleRate?: string | number
  replaySampleRate?: string | number
  profilingSampleRate?: string | number
  /** Controlled collection settings; canonical identity and sanitization remain owned here. */
  sdkOptions?: Pick<
    RumInitConfiguration,
    | 'telemetrySampleRate'
    | 'trackResources'
    | 'trackUserInteractions'
    | 'defaultPrivacyLevel'
    | 'beforeSend'
  >
}): boolean {
  setRumVitalsEnabled(false)
  configureClientMetrics(true)
  if (!args.applicationId?.trim() || !args.clientToken?.trim()) {
    return false
  }

  try {
    const sessionSampleRate = parseSampleRate(args.sessionSampleRate, 100)
    const sdkOptions = args.sdkOptions
    const beforeSend = sdkOptions?.beforeSend
    if (sessionSampleRate > 0) {
      installBrowserLifecycleDrain()
    }
    datadogRum.init({
      applicationId: args.applicationId,
      clientToken: args.clientToken,
      site: args.site ?? 'datadoghq.com',
      service: args.service,
      env: args.env,
      version: args.version,
      sessionSampleRate,
      sessionReplaySampleRate: parseSampleRate(args.replaySampleRate, 20),
      // JS self-profiling is Chromium-only and additionally gated on the
      // `Document-Policy: js-profiling` response header both Next configs send.
      profilingSampleRate: parseSampleRate(args.profilingSampleRate, 5),
      telemetrySampleRate: sdkOptions?.telemetrySampleRate,
      trackResources: sdkOptions?.trackResources ?? true,
      trackUserInteractions: sdkOptions?.trackUserInteractions ?? true,
      trackLongTasks: true,
      compressIntakeRequests: true,
      defaultPrivacyLevel: sdkOptions?.defaultPrivacyLevel ?? 'mask-user-input',
      traceContextInjection: 'sampled',
      propagateTraceBaggage: false,
      allowedTracingUrls: buildAllowedTracingUrls({
        origin: args.origin,
        additionalPrefixes: args.additionalTracingPrefixes,
      }),
      plugins: [nextjsPlugin()],
      beforeSend: (event, context) => {
        if (!sanitizeRumEvent(event as RumSanitizableEvent)) {
          return false
        }
        try {
          return beforeSend?.(event, context) ?? true
        } catch {
          incrementTelemetryHealth('client_lifecycle_failed')
          return false
        }
      },
    })
    setRumVitalsEnabled(sessionSampleRate > 0)
  } catch {
    return false
  }
  return true
}

function updateRumIdentity(operation: () => void): void {
  try {
    operation()
  } catch {
    // Identity attribution is optional; SDK failures cannot escape auth effects.
    incrementTelemetryHealth('client_lifecycle_failed')
  }
}

export function setRumIdentity(args: {
  user: User & { id: string }
  account?: Account & { id: string }
}): void {
  updateRumIdentity(() => datadogRum.setUser(args.user))
  updateRumIdentity(() => {
    const account = args.account
    if (account) {
      datadogRum.setAccount(account)
    } else {
      datadogRum.clearAccount()
    }
  })
}

export function clearRumIdentity(): void {
  updateRumIdentity(() => datadogRum.clearUser())
  updateRumIdentity(() => datadogRum.clearAccount())
}

export function addRumAction(
  name: string,
  context?: Record<string, unknown>
): void {
  try {
    datadogRum.addAction(
      name,
      sanitizeLogValue(context ?? {}) as Record<string, unknown>
    )
  } catch {
    // RUM delivery cannot interrupt browser interactions.
  }
}

export function addRumError(
  error: unknown,
  context?: Record<string, unknown>
): void {
  try {
    const diagnostic = createRumErrorDiagnostic(error)
    datadogRum.addError(
      diagnostic.error,
      rumErrorContext(context, diagnostic.aggregation)
    )
  } catch {
    // Reporting an error must not replace the product's original error.
  }
}

function rumErrorContext(
  context: unknown,
  aggregation: ReturnType<typeof createRumErrorDiagnostic>['aggregation']
): Record<string, unknown> {
  let safeContext: Record<string, unknown> = {}
  try {
    const sanitized = sanitizeLogValue(
      context && typeof context === 'object' && !Array.isArray(context)
        ? context
        : {},
      rumCorrelationIdPaths(context)
    )
    if (
      sanitized &&
      typeof sanitized === 'object' &&
      !Array.isArray(sanitized)
    ) {
      safeContext = sanitized as Record<string, unknown>
    }
  } catch {
    // Unavailable optional context must not erase the primary diagnostic.
  }
  if (aggregation.length > 0) {
    // RUM ignores AggregateError.errors; retain a bounded member summary.
    safeContext.error_aggregation = sanitizeLogValue(aggregation)
  }
  return safeContext
}

function optionalErrorField(error: Error, key: string): unknown {
  try {
    return Reflect.get(error, key)
  } catch {
    return undefined
  }
}

export function addRumNextjsError(error: Error & { digest?: string }): void {
  try {
    // Next's plugin captures causes before beforeSend can redact them. Keep
    // framework correlation on the sanitized copy, never on the business Error.
    const diagnostic = createRumErrorDiagnostic(error)
    const digest = optionalErrorField(error, 'digest')
    const reported = Object.assign(diagnostic.error, {
      ...(typeof digest === 'string'
        ? { digest: truncate(redactSensitiveString(digest), 4096) }
        : {}),
      dd_context: rumErrorContext(
        optionalErrorField(error, 'dd_context'),
        diagnostic.aggregation
      ),
    })
    addNextjsError(reported)
  } catch {
    // Error boundaries must render even when the telemetry SDK is unavailable.
  }
}
